import React, { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import {
  Calendar,
  ChevronUp,
  FileText,
  PlusCircle,
  RotateCcw,
  Sliders,
  StopCircle,
  User,
} from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../core/redux/action";
import { useDispatch, useSelector } from "react-redux";
import { Filter, MinusCircle } from "react-feather";
import Select from "react-select";
import { DatePicker } from "antd";
import { saleslist } from "../../core/json/saleslistdata";
import Table from "../../core/pagination/datatable";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import AddCustomerTransaction from "./addCustomerTransaction";
import { useGetCustomerTransactionQuery } from "../../core/redux/api/OrderApi";
import { useGetAllCustomerQuery } from "../../core/redux/api/userApi";
import './transaction.css';
import { exportToExcel, exportToPDF } from "../utils/exportUtils2";
import { render } from "@fullcalendar/core/preact.js";

const CustomerTransactions = () => {
  const isMobile = window.innerWidth < 768;
  const saleslistdata = saleslist;
  const { data: CustomerTransactionsData, isLoading, refetch } = useGetCustomerTransactionQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true
  });
  const [exportLoading, setExportLoading] = useState(false);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.rootReducer.toggle_header);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  const [dateRange, setDateRange] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const { data: customers } = useGetAllCustomerQuery();

  // Filter transactions based on filters (date range, customer, type)
  const filteredData = useMemo(() => {
    if (!CustomerTransactionsData) return [];

    return CustomerTransactionsData.filter(transaction => {
      // Date filter
      if (dateRange?.[0] && dateRange?.[1]) {
        const transactionDate = new Date(transaction.createdAt);
        const startDate = new Date(dateRange[0]);
        const endDate = new Date(dateRange[1]);

        if (transactionDate < startDate || transactionDate > endDate) {
          return false;
        }
      }

      // Customer filter
      if (selectedCustomer && transaction.customerId?._id !== selectedCustomer._id) {
        return false;
      }

      // Type filter
      if (selectedType && transaction.type !== selectedType.value) {
        return false;
      }

      return true;
    });
  }, [CustomerTransactionsData, dateRange, selectedCustomer, selectedType]);



  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );
  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );
  const renderPrinterTooltip = (props) => (
    <Tooltip id="printer-tooltip" {...props}>
      Printer
    </Tooltip>
  );
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );
  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );
  const MySwal = withReactContent(Swal);


  const customerTransactionColumns = [
    {
      title: "Customer Name",
      dataIndex: ["customerId", "customerName"],
      render: (customerName, record) => (
        <span>
          {customerName || record.customerId?.customerName || "-"}
        </span>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Amount",
      dataIndex: "amount",
      sorter: (a, b) => a.amount - b.amount,
      render: (amount, record) => (
        <span style={{ color: record.type === 'credit' ? 'green' : 'red' }}>
          {Number(amount).toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
          })}
        </span>
      ),
      render : (amount) => {
        if (amount === null || amount === undefined) return 'N/A';
        return '₹' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      },
      filters: [
        { text: 'Greater than 0', value: 'greater' },
        { text: 'Less than 0', value: 'less' },
      ],
      onFilter: (value, record) => {
        if (value === 'greater') {
          return record.amount > 0;
        } else if (value === 'less') {
          return record.amount < 0;
        }
        return true; // Default case, no filter applied
      },      
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (type) => (
        <span style={{ color: type === 'credit' ? 'green' : 'red' }}>
          {type.toUpperCase()}
        </span>
      ),
      filters: [
        { text: 'credit', value: 'credit' },
        { text: 'debit', value: 'debit' },
      ],
      onFilter: (value, record) => record.type === value,
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (date) => (
        <span>{new Date(date).toLocaleDateString()}, {new Date(date).toLocaleTimeString()}</span>
      ),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (remarks) => remarks || "-",
      responsive: ['sm', 'md', 'lg', 'xl'],
    }
  ];

  const expandable = {
    expandedRowRender: (record) => (
      <div style={{ padding: '10px' }}>
        <p><strong>Type:</strong> <span style={{ color: record.type === 'credit' ? 'green' : 'red' }}>{record.type}</span></p>
        <p><strong>Date:</strong> {new Date(record.createdAt).toLocaleDateString()}, {new Date(record.createdAt).toLocaleTimeString()}</p>
        <p><strong>Remarks:</strong> {record.remarks || "-"}</p>
      </div>
    ),
    rowExpandable: (record) => true,
    expandRowByClick: true,
  };

  const pdfOptions = {
    title: 'Customer Transactions Report',
    columns: [
      { key: 'customerName', header: 'Customer Name' },
      {
        key: 'amount',
        header: 'Amount',
        format: (val) => {
          if (val === null || val === undefined) return 'N/A';
          return '₹' + val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
      },
      { key: 'type', header: 'Type' },
      { key: 'date', header: 'Date' },
      { key: 'remarks', header: 'Remarks' }
    ],
    data: filteredData.map(transaction => ({
      customerName: transaction.customerId?.customerName || 'N/A',
      amount: transaction.amount,
      type: transaction.type,
      date: new Date(transaction.createdAt).toLocaleDateString(),
      remarks: transaction.remarks || '-'
    })),
    fileName: 'customer_transactions_report',
    orientation: 'landscape'
  };

  const excelOptions = {
    title: 'Customer Transactions Report',
    columns: [
      { key: 'customerName', header: 'Customer Name' },
      { key: 'amount', header: 'Amount', format: (val) => val?.toFixed(2) || '0.00' },
      { key: 'type', header: 'Type' },
      { key: 'date', header: 'Date' },
      { key: 'remarks', header: 'Remarks' }
    ],
    data: filteredData.map(transaction => ({
      customerName: transaction.customerId?.customerName || 'N/A',
      amount: transaction.amount,
      type: transaction.type,
      date: new Date(transaction.createdAt).toLocaleDateString(),
      remarks: transaction.remarks || '-'
    })),
    fileName: 'customer_transactions',
    sheetName: 'Transactions'
  };

  if (isLoading) {
    return <div className="text-center py-5">Loading transactions...</div>;
  }

  if (!CustomerTransactionsData) {
    return <div className="text-center py-5">No transaction data available</div>;
  }

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Customer Ledger</h4>
                <h6>Manage Your Customer's Ledger</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip}>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      exportToPDF(pdfOptions);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="PDF Export" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      exportToExcel(excelOptions);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {exportLoading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <ImageWithBasePath src="assets/img/icons/excel.svg" alt="Excel Export" />
                    )}
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link data-bs-toggle="tooltip" data-bs-placement="top">
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      dispatch(setToogleHeader(!data));
                    }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-added"
                data-bs-toggle="modal"
                data-bs-target="#add-customer-transaction"
              >
                <i className='ti ti-circle-plus me-1'></i>
                Add New Transaction
              </Link>
            </div>
          </div>

          <div className="card table-list-card">
            <div className="card-body">
              <div className="card border-0">
                <div className="card-body pb-1">
                  <form onSubmit={(e) => e.preventDefault()}>
                    <div className="row align-items-end">
                      <div className="col-lg-10">
                        <div className="row">
                          <div className="col-md-4">
                            <div className="mb-3">
                              <label className="form-label">Date Range</label>
                              {isMobile ? (
                                <>
                                  <DatePicker
                                    className="form-control mb-2"
                                    placeholder="Start Date"
                                    onChange={(date) => setDateRange([date, dateRange?.[1]])}
                                  />
                                  <DatePicker
                                    className="form-control"
                                    placeholder="End Date"
                                    onChange={(date) => setDateRange([dateRange?.[0], date])}
                                  />
                                </>
                              ) : (
                                <DatePicker.RangePicker
                                  className="form-control"
                                  onChange={setDateRange}
                                  style={{ width: '100%' }}
                                />
                              )}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="mb-3">
                              <label className="form-label">Customer</label>
                              <Select
                                classNamePrefix="react-select"
                                options={customers}
                                placeholder="All Customers"
                                value={selectedCustomer}
                                onChange={setSelectedCustomer}
                                getOptionLabel={(option) => option.customerName}
                                getOptionValue={(option) => option._id}
                                isClearable
                              />
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="mb-3">
                              <label className="form-label">Transaction Type</label>
                              <Select
                                classNamePrefix="react-select"
                                options={[
                                  { value: '', label: 'All Types' },
                                  { value: 'credit', label: 'credit' },
                                  { value: 'debit', label: 'debit' }
                                ]}
                                placeholder="All Types"
                                value={selectedType}
                                onChange={setSelectedType}
                                isClearable
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-2">
                        <div className="mb-3">
                          <button
                            className="btn btn-primary w-100"
                            type="button"
                            onClick={() => {
                              setDateRange(null);
                              setSelectedCustomer(null);
                              setSelectedType(null);
                            }}
                          >
                            Reset Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              <div className="table-responsive">
                <Table
                  columns={customerTransactionColumns}
                  dataSource={filteredData}
                  expandable={expandable}
                // Let the DataTable handle searching internally
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddCustomerTransaction />
    </div>
  );
};

export default CustomerTransactions;